/**
 * Quick setup block 7 — Pricing & payment (plan 6.C, paid only).
 * Writes: pricing[0].price, event_prepaid_status / payments_* / deposit_* (6.H table), refund_*.
 */

import React from "react";
import Icon from "../../ui/Icon";
import { FieldError } from "../../ui/primitives";
import { E, PAYMENT_APP_IDS, QUICK_REFUND_OPTIONS, SECTIONS, STEPS } from "../../state/constants";
import { currency } from "../../state/selectors";
import { SetupBlock } from "./SetupBlocks";
import useQuickSetup, { paymentTiming, quickRefundValue } from "./useQuickSetup";

/** Connected gateways from connected-venues (app ids 10/11, connection_status E) — informational only. */
export const providerRows = (connectedVenues) => {
  const on = (appId) => (connectedVenues || []).some((v) => parseInt(v.app_id, 10) === appId && (v.connection_status === undefined || v.connection_status === E));
  const rows = [];
  if (on(PAYMENT_APP_IDS.STRIPE)) rows.push("Stripe");
  if (on(PAYMENT_APP_IDS.PAYPAL)) rows.push("PayPal");
  return rows;
};

const PaymentBlock = ({ n = 7 }) => {
  const { state, setPrice, setTiming, setDepositPct, setQuickRefund, goToSection, issues } = useQuickSetup();
  const d = state.details;
  const cur = currency(state);
  const first = (d.pricing || [])[0] || {};
  const timing = paymentTiming(d);
  const refund = quickRefundValue(d);
  const providers = providerRows(state.lookups.connectedVenues);
  const touched = state.save.version[STEPS.DETAILS] > 0 || !state.meta.isNew;
  const kindIssue = issues.find((i) => i.field === "kind"); // plan / gateway gate — always visible
  const priceIssue = issues.find((i) => i.field === "price");
  const depositIssue = issues.find((i) => i.field === "deposit_price");
  const priceNum = Number(first.price) || 0;
  const pct = Number(d.deposit_price) || 0;
  const due = priceNum && pct ? Math.round((priceNum * pct) / 100 * 100) / 100 : 0;
  const refundLabel = (QUICK_REFUND_OPTIONS.find((o) => o.value === refund) || {}).label || (refund === "partial" ? "Partial refund" : "Choose a policy");

  return (
    <SetupBlock id="payment-setting" n={n} paid highlight={state.ui.highlight === "payment-setting"}>
      <div className="eb-paid-field-title">
        <div>
          <label>Pricing &amp; payment</label>
          <p>Set the amount and how payment will be collected.</p>
        </div>
        <span>
          <Icon name="shield" size={14} /> Secure payments
        </span>
      </div>
      <FieldError message={kindIssue ? kindIssue.message : null} />

      <div className="eb-price-row">
        <label>
          Event price
          <div className={`eb-money-field ${priceIssue && touched ? "eb-has-error" : ""}`.trim()}>
            <span>{cur}</span>
            <input type="number" min="1" step="0.01" value={first.price === undefined ? "" : first.price} onChange={(e) => setPrice(e.target.value)} aria-label="Event price" />
          </div>
          <FieldError message={priceIssue && touched ? priceIssue.message : null} />
        </label>
        <label>
          Payment provider
          <div className="eb-provider-select">
            <span className={providers[0] === "PayPal" ? "eb-paypal-mark" : "eb-stripe-mark"}>{providers[0] === "PayPal" ? "P" : "S"}</span>
            <select value={providers[0] || ""} onChange={() => {}} aria-label="Payment provider" disabled={providers.length <= 1}>
              {providers.length ? providers.map((p) => <option key={p}>{p}</option>) : <option value="">Not connected</option>}
            </select>
            <em>{providers.length ? "Connected" : "Connect"}</em>
          </div>
        </label>
      </div>

      <label className="eb-payment-label">When should payment be collected?</label>
      <div className="eb-payment-timing" role="radiogroup" aria-label="Payment timing">
        {[
          { key: "full", title: "Full payment", sub: "Charge the complete amount when booked" },
          { key: "deposit", title: "Deposit", sub: "Collect part now and the rest later" },
          { key: "at_event", title: "Pay at event", sub: "Confirm without online payment" },
        ].map((o) => (
          <button type="button" key={o.key} role="radio" aria-checked={timing === o.key} className={timing === o.key ? "selected" : ""} onClick={() => setTiming(o.key)}>
            <i />
            <span>
              <strong>{o.title}</strong>
              <small>{o.sub}</small>
            </span>
          </button>
        ))}
      </div>

      {timing === "deposit" ? (
        <label className="eb-deposit-field">
          Deposit amount
          <div>
            <input type="number" min="1" max="99" value={d.deposit_price === undefined ? "" : d.deposit_price} onChange={(e) => setDepositPct(e.target.value)} aria-label="Deposit percentage" />
            <span>%</span>
            <small>
              {cur} {due} due at booking
            </small>
          </div>
          <FieldError message={depositIssue && touched ? depositIssue.message : null} />
        </label>
      ) : null}

      <div className="eb-refund-quick">
        <div>
          <Icon name="shield-check" size={17} />
          <span>
            <strong>Refund policy</strong>
            <small>{refundLabel}</small>
          </span>
        </div>
        <select value={refund} onChange={(e) => setQuickRefund(e.target.value)} aria-label="Refund policy">
          {refund === "" ? <option value="">Choose a policy</option> : null}
          {QUICK_REFUND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {refund === "partial" ? (
            <option value="partial" disabled>
              Partial refund (set on Payments)
            </option>
          ) : null}
        </select>
      </div>

      <button type="button" className="eb-more-payment" onClick={() => goToSection(SECTIONS.PAYMENTS)}>
        More payment options <Icon name="chevron" size={15} />
      </button>
    </SetupBlock>
  );
};

export default PaymentBlock;
